import { tv, type VariantProps } from 'tailwind-variants'

import { dataFocusVisibleClasses } from '@/src/styles/classes'

export const chipVariants = tv({
  slots: {
    background: 'gap-1 flex flex-row w-fit items-center justify-center transition-all flex-0',
    text: 'overflow-hidden w-fit text-ellipsis whitespace-nowrap px-0.5',
    badgeBackground: 'min-w-5 min-h-5 rounded-full flex items-center justify-center px-1.5',
    badgeText: '',
    textColor: '',
  },
  variants: {
    colors: {
      default: {
        background: 'bg-default-bg-inverse',
        textColor: 'text-default-label-solid',
        badgeBackground: 'bg-default-bg-light',
        badgeText: 'text-default-label-light-contrast-low',
      },
      primary: {
        background: 'bg-primary-bg-inverse',
        textColor: 'text-primary-label-solid',
        badgeBackground: 'bg-primary-bg-light',
        badgeText: 'text-primary-label-light-contrast-low',
      },
      secondary: {
        background: 'bg-secondary-bg-inverse',
        textColor: 'text-secondary-label-solid',
        badgeBackground: 'bg-secondary-bg-light',
        badgeText: 'text-secondary-label-light-contrast-low',
      },
      success: {
        background: 'bg-success-bg-inverse',
        textColor: 'text-success-label-solid',
        badgeBackground: 'bg-success-bg-light',
        badgeText: 'text-success-label-light-contrast-low',
      },
      danger: {
        background: 'bg-danger-bg-inverse',
        textColor: 'text-danger-label-solid',
        badgeBackground: 'bg-danger-bg-light',
        badgeText: 'text-danger-label-light-contrast-low',
      },
    },
    variant: {
      solid: { background: 'border border-layout-stroke-contrast-low' },
      flat: { background: 'border border-layout-stroke-contrast-medium' },
    },
    hasText: {
      true: '',
      false: '',
    },
    hasIcon: {
      true: '',
      false: '',
    },
    hasBadge: {
      true: '',
      false: '',
    },
    size: {
      sm: { background: 'px-1.5 rounded-sm min-h-6' },
      md: { background: 'px-2 rounded-md min-h-7' },
    },
    shrinkDisabled: {
      true: {
        background: 'flex-none',
      },
      false: {
        background: '',
      },
    },
    isPressable: {
      true: {
        background: dataFocusVisibleClasses,
      },
      false: {
        background: 'cursor-auto',
      },
    },
  },
  defaultVariants: {
    colors: 'default',
    variant: 'solid',
    hasIcon: false,
    hasBadge: false,
  },
  compoundVariants: [
    /* Color Variants - Flat */
    {
      colors: 'default',
      variant: 'flat',
      class: {
        background: 'bg-default-bg-light',
        textColor: 'text-default-label-light',
        badgeBackground: 'bg-default-bg-medium',
        badgeText: 'text-default-label-light',
      },
    },
    {
      colors: 'primary',
      variant: 'flat',
      class: {
        background: 'bg-primary-bg-light',
        textColor: 'text-primary-label-light',
        badgeBackground: 'bg-primary-bg-medium',
        badgeText: 'text-primary-label-light',
      },
    },
    {
      colors: 'secondary',
      variant: 'flat',
      class: {
        background: 'bg-secondary-bg-light',
        textColor: 'text-secondary-label-light',
        badgeBackground: 'bg-secondary-bg-medium',
        badgeText: 'text-secondary-label-light',
      },
    },
    {
      colors: 'success',
      variant: 'flat',
      class: {
        background: 'bg-success-bg-light',
        textColor: 'text-success-label-light',
        badgeBackground: 'bg-success-bg-medium',
        badgeText: 'text-success-label-light',
      },
    },
    {
      colors: 'danger',
      variant: 'flat',
      class: {
        background: 'bg-danger-bg-light',
        textColor: 'text-danger-label-light',
        badgeBackground: 'bg-danger-bg-medium',
        badgeText: 'text-danger-label-light',
      },
    },
    {
      hasBadge: true,
      hasIcon: true,
      class: {
        background: 'shrink min-w-0',
        text: 'shrink min-w-0',
      },
    },
    {
      hasIcon: true,
      class: {
        background: 'pl-1',
      },
    },
    {
      hasIcon: true,
      hasBadge: false,
      class: {
        background: 'pl-1 pr-2',
      },
    },
    {
      hasBadge: true,
      class: {
        background: 'pl-2 pr-1',
      },
    },
    {
      hasIcon: true,
      hasBadge: true,
      class: {
        background: 'pl-1 pr-1',
      },
    },
    {
      hasIcon: false,
      hasBadge: true,
      hasText: false,
      class: {
        background: 'pl-[2px] pr-[2px]',
      },
    },
    {
      hasIcon: true,
      hasBadge: false,
      hasText: false,
      class: {
        background: 'pl-[2px] pr-[2px]',
      },
    },
    /* Small Size Variants */
    {
      hasBadge: true,
      size: 'sm',
      class: {
        background: 'pr-[2px]',
      },
    },
  ],
})

export type TChipVariantProps = VariantProps<typeof chipVariants>
export type TChipSlots = keyof ReturnType<typeof chipVariants>
